/* Fatal team kit order form: rows of (jersey size, bib size, optional add-on size, qty) -> one cart request. */
if (!customElements.get('fatal-kit-builder')) {
  customElements.define(
    'fatal-kit-builder',
    class FatalKitBuilder extends HTMLElement {
      connectedCallback() {
        this.variants = JSON.parse(this.querySelector('[data-kit-variants]').textContent);
        const addonData = this.querySelector('[data-kit-addon]');
        this.addonVariants = addonData ? JSON.parse(addonData.textContent) : [];
        this.addonLabel = this.dataset.addonLabel || 'Add-on';
        this.list = this.querySelector('[data-kit-list]');
        this.rowTemplate = this.querySelector('[data-kit-row]');
        this.submitButton = this.querySelector('[data-kit-submit]');
        this.errorEl = this.querySelector('[data-kit-error]');
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

        this.querySelector('[data-add-row]').addEventListener('click', () => this.addRow(true));
        this.list.addEventListener('click', (e) => this.onListClick(e));
        this.list.addEventListener('change', () => this.update());
        this.list.addEventListener('input', () => this.update());
        this.submitButton.addEventListener('click', () => this.submit());

        this.addRow(false);
      }

      addRow(focus) {
        const row = this.rowTemplate.content.firstElementChild.cloneNode(true);
        const last = this.list.lastElementChild;
        if (last) {
          // Start a new line from the previous selection; the customer usually tweaks one size.
          row.querySelector('[data-o1]').value = last.querySelector('[data-o1]').value;
          row.querySelector('[data-o2]').value = last.querySelector('[data-o2]').value;
          const addon = row.querySelector('[data-addon-size]');
          if (addon) addon.value = last.querySelector('[data-addon-size]').value;
        }
        this.list.appendChild(row);
        this.update();
        if (focus) row.querySelector('[data-o1]').focus();
      }

      onListClick(e) {
        const row = e.target.closest('.fx-kit__row');
        if (!row) return;
        const qty = row.querySelector('[data-qty]');
        if (e.target.closest('[data-plus]')) qty.value = Math.min(99, (parseInt(qty.value, 10) || 0) + 1);
        if (e.target.closest('[data-minus]')) qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) - 1);
        if (e.target.closest('[data-remove]')) {
          if (this.list.children.length > 1) {
            const prev = row.previousElementSibling || row.nextElementSibling;
            row.remove();
            prev && prev.querySelector('[data-o1]').focus();
          } else {
            qty.value = 1;
          }
        }
        this.update();
      }

      findVariant(o1, o2) {
        return this.variants.find((v) => v.o1 === o1 && v.o2 === o2);
      }

      // Merge rows with the same size combo so the cart gets one line per variant.
      // Add-ons are collected separately so the summary can count them on their own.
      collect() {
        const items = new Map();
        const addons = new Map();
        let problem = null;
        [...this.list.children].forEach((row) => {
          const o1 = row.querySelector('[data-o1]').value;
          const o2 = row.querySelector('[data-o2]').value;
          const qty = parseInt(row.querySelector('[data-qty]').value, 10);
          const variant = this.findVariant(o1, o2);
          row.classList.remove('is-invalid');
          if (!variant || !variant.available) {
            row.classList.add('is-invalid');
            problem = `${o1} jersey / ${o2} bibs isn't available.`;
            return;
          }
          if (!qty || qty < 1) {
            row.classList.add('is-invalid');
            problem = 'Each line needs a quantity of at least 1.';
            return;
          }
          const existing = items.get(variant.id);
          items.set(variant.id, { variant, quantity: (existing ? existing.quantity : 0) + qty });

          const addonSelect = row.querySelector('[data-addon-size]');
          if (addonSelect && addonSelect.value) {
            const addon = this.addonVariants.find((v) => v.o1 === addonSelect.value);
            if (!addon || !addon.available) {
              row.classList.add('is-invalid');
              problem = `${this.addonLabel} in ${addonSelect.value} isn't available.`;
              return;
            }
            const prev = addons.get(addon.id);
            addons.set(addon.id, { variant: addon, quantity: (prev ? prev.quantity : 0) + qty });
          }
        });
        return { items: [...items.values()], addons: [...addons.values()], problem };
      }

      update() {
        const { items, addons, problem } = this.collect();
        if (!problem && !this.errorEl.hidden) this.showError('');
        const count = items.reduce((n, i) => n + i.quantity, 0);
        const addonCount = addons.reduce((n, i) => n + i.quantity, 0);
        const total = [...items, ...addons].reduce((n, i) => n + i.quantity * i.variant.price, 0);
        this.querySelector('[data-kit-count]').textContent = `${count} ${count === 1 ? 'kit' : 'kits'}`;
        const addonEl = this.querySelector('[data-kit-addon-count]');
        if (addonEl) {
          addonEl.hidden = addonCount === 0;
          addonEl.textContent = ` + ${addonCount} ${this.addonLabel.toLowerCase()}${addonCount === 1 ? '' : 's'}`;
        }
        this.querySelector('[data-kit-total]').textContent = this.formatMoney(total);
        this.list.querySelectorAll('[data-remove]').forEach((b) => (b.hidden = this.list.children.length === 1));
      }

      formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        const withCommas = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const [whole, dec] = amount.split('.');
        return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
          if (key === 'amount_no_decimals') return withCommas(Math.round(cents / 100).toString());
          if (key === 'amount_with_comma_separator') return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`;
          return `${withCommas(whole)}.${dec}`;
        });
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = !message;
      }

      async submit() {
        const { items, addons, problem } = this.collect();
        if (problem || !items.length) {
          this.showError(problem || 'Add at least one kit.');
          return;
        }
        this.showError('');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');

        const properties = {};
        if (this.dataset.propertyName && this.dataset.propertyValue) {
          properties[this.dataset.propertyName] = this.dataset.propertyValue;
        }
        const cartDrawer = document.querySelector('cart-drawer');
        const body = {
          items: [...items, ...addons].map((i) => ({ id: i.variant.id, quantity: i.quantity, properties })),
        };
        if (cartDrawer) {
          body.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }

        try {
          const res = await fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (!res.ok || data.status) {
            this.showError(data.description || data.message || 'Something went wrong adding your kit. Please try again.');
            return;
          }
          if (cartDrawer && data.sections) {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(data);
          } else {
            window.location = window.routes ? window.routes.cart_url : '/cart';
          }
        } catch (e) {
          this.showError('Something went wrong adding your kit. Please try again.');
        } finally {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
        }
      }
    }
  );
}
